<template>
  <CollapsibleSection
    title="Dietary Information"
    :badge="filledLinesCount || null"
    section-key="dietary-info"
    @toggle="handleToggle"
  >
    <div class="dietary-lines-list">
      <div class="pb-4">
        <button
          type="button"
          class="button is-info is-outlined is-fullwidth is-mini"
          @click="handleImportClick"
        >
          <span class="icon is-small">
            <i class="fa-sharp-duotone fa-solid fa-database"></i>
          </span>
          <span>Import from Recipe</span>
        </button>
      </div>

      <WarningsBox :compliance="dietaryComplianceWarnings" :informational="dietaryInfoWarnings" />

      <div
        v-for="line in store.dietaryLines"
        :key="line.dv_key"
        class="columns is-mobile is-vcentered mb-2 dietary-line"
        :class="{
          'dietary-line--indented': isIndented(line.dv_key),
          'dietary-line--double-indented': isDoubleIndented(line.dv_key),
          'dietary-line--calories': line.dv_key === 'calories',
          'dietary-line--compliance': complianceDvKeys.has(line.dv_key),
          'dietary-line--warned': !complianceDvKeys.has(line.dv_key) && infoDvKeys.has(line.dv_key),
        }"
      >
        <div class="column dietary-line-label">
          <span v-if="line.dv_key === 'protein'" class="icon-text is-size-7-desktop">
            <label :for="`dietary-${line.dv_key}-input`">{{ getLabel(line.dv_key) }}</label>
            <IconHint class="protein-hint" :content="proteinAmountTooltip" interactive />
          </span>
          <label v-else :for="`dietary-${line.dv_key}-input`" class="is-size-7-desktop">{{
            getLabel(line.dv_key)
          }}</label>
        </div>
        <div class="column is-narrow">
          <div class="field has-addons mb-0 is-justify-content-end">
            <p class="control">
              <input
                :id="`dietary-${line.dv_key}-input`"
                type="number"
                inputmode="decimal"
                min="0"
                step="any"
                autocomplete="off"
                class="input is-size-7-desktop dietary-input"
                :value="line.amount"
                :placeholder="getPlaceholder(line.dv_key)"
                :aria-label="getLabel(line.dv_key)"
                @input="updateAmount(line.dv_key, $event.target.value)"
                @keydown="preventScientificNotation"
              />
            </p>
            <p v-if="line.dv_key !== 'calories'" class="control">
              <span
                class="button is-static dietary-unit"
                :class="{ 'dietary-unit--stacked': ['mcg RAE', 'mcg DFE'].includes(line.unit) }"
              >
                <template v-if="line.unit === 'mcg RAE'">
                  <span class="unit-part">mcg</span>
                  <span class="unit-part">RAE</span>
                </template>
                <template v-else-if="line.unit === 'mcg DFE'">
                  <span class="unit-part">mcg</span>
                  <span class="unit-part">DFE</span>
                </template>
                <template v-else>
                  <span class="is-size-7-desktop">{{ line.unit }}</span>
                </template>
              </span>
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="hasProteinAmount"
        class="columns is-mobile is-vcentered mb-2 dietary-line dietary-line--no-unit"
      >
        <div class="column dietary-line-label">
          <span class="icon-text is-size-7-desktop">
            <label for="protein-score-input"
              >Protein Score <small><i>(0.0-1.0)</i></small></label
            >
            <IconHint class="protein-hint" :content="proteinScoreTooltip" />
          </span>
        </div>
        <div class="column is-narrow">
          <div class="field has-addons mb-0 is-justify-content-end">
            <p class="control">
              <input
                id="protein-score-input"
                type="number"
                inputmode="decimal"
                min="0"
                max="1"
                step="any"
                autocomplete="off"
                class="input is-size-7-desktop dietary-input"
                :value="store.proteinScore"
                placeholder="1.0"
                aria-label="Protein Score (PDCAAS)"
                @input="updateProteinScore($event.target.value)"
                @blur="clampProteinScoreInput($event.target)"
                @keydown="preventScientificNotation"
              />
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="filledLinesCount > 0" class="buttons mt-3 mb-0 is-hidden-touch">
      <ClearAllButton clear-type="dietary" />
    </div>
  </CollapsibleSection>
</template>

<script setup>
  import { computed } from "vue";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import CollapsibleSection from "./collapsible-section.vue";
  import WarningsBox from "./warnings-box.vue";
  import ClearAllButton from "./clear-all-button.vue";
  import IconHint from "../icon-hint.vue";
  import { getDietaryLabel, getDietaryIndentLevel } from "./utils/nutrients";
  import { preventScientificNotation } from "./utils/formatting";
  import { focusOnSectionOpen } from "./utils/focus-helpers";

  const store = useSupplementLabelStore();

  const dietaryComplianceWarnings = computed(() =>
    store.dietaryWarnings.filter((w) => !w.informational).map((w) => w.message)
  );
  const dietaryInfoWarnings = computed(() =>
    store.dietaryWarnings.filter((w) => w.informational).map((w) => w.message)
  );
  const complianceDvKeys = computed(
    () =>
      new Set(
        store.dietaryWarnings.filter((w) => w.dv_key && !w.informational).map((w) => w.dv_key)
      )
  );
  const infoDvKeys = computed(
    () =>
      new Set(store.dietaryWarnings.filter((w) => w.dv_key && w.informational).map((w) => w.dv_key))
  );

  const proteinAmountTooltip =
    "If your product contains only individual amino acids (no intact protein), " +
    "do not declare protein here. Instead, list each amino acid by name under " +
    "'Ingredients without DVs' " +
    '(<a href="https://www.ecfr.gov/current/title-21/section-101.36#p-101.36(b)(2)(i)" ' +
    'target="_blank" rel="noopener">21 CFR 101.36(b)(2)(i)</a>).';

  const PROTEIN_SCORE_EXPLANATION =
    "This is a PDCAAS value that adjusts the protein %DV " +
    "based on protein digestibility. " +
    "FDA requires protein %DV on Supplement Facts labels to be PDCAAS-corrected, " +
    "so make sure this value is accurate -- especially if you're making a protein " +
    "claim. You may need to work with a lab or research your protein source to " +
    "determine the correct value.";

  const proteinScoreTooltip = computed(() => {
    if (store.proteinScore === null || store.proteinScore === "") {
      return (
        "Protein %DV is hidden because no Protein Score has been set. " + PROTEIN_SCORE_EXPLANATION
      );
    }
    return PROTEIN_SCORE_EXPLANATION;
  });

  const emit = defineEmits(["open-recipe-import"]);

  function handleImportClick() {
    store.debugLog("DietaryInformationSection: Import button clicked");
    emit("open-recipe-import");
  }

  const PLACEHOLDERS = {
    calories: "e.g., 50",
    fat: "e.g., 2",
    fat_saturated: "e.g., 0.5",
    fat_trans: "e.g., 0",
    fat_polyunsaturated: "e.g., 0.5",
    fat_monounsaturated: "e.g., 1",
    cholesterol: "e.g., 5",
    carbohydrate: "e.g., 8",
    new_fiber: "e.g., 1",
    sugar: "e.g., 6",
    added_sugar: "e.g., 4",
    protein: "e.g., 3",
  };

  function getLabel(dvKey) {
    return getDietaryLabel(dvKey, store);
  }

  function getPlaceholder(dvKey) {
    return PLACEHOLDERS[dvKey] || "";
  }

  function isIndented(dvKey) {
    return getDietaryIndentLevel(dvKey, store) === 1;
  }

  function isDoubleIndented(dvKey) {
    return getDietaryIndentLevel(dvKey, store) === 2;
  }

  const hasProteinAmount = computed(() => {
    const proteinLine = store.dietaryLines.find((l) => l.dv_key === "protein");
    return (
      proteinLine &&
      proteinLine.amount !== "" &&
      proteinLine.amount !== null &&
      proteinLine.amount !== undefined
    );
  });

  const filledLinesCount = computed(() => {
    return store.dietaryLines.filter((l) => l.amount).length;
  });

  function updateAmount(dvKey, value) {
    store.updateDietaryLine(dvKey, value);
  }

  function updateProteinScore(value) {
    if (value === "" || value === null || value === undefined) {
      store.proteinScore = null;
      return;
    }
    const num = parseFloat(value);
    if (!isNaN(num)) {
      store.proteinScore = Math.min(1, Math.max(0, num));
    }
  }

  function clampProteinScoreInput(input) {
    if (store.proteinScore != null) {
      input.value = store.proteinScore;
    }
  }

  function handleToggle(isOpen, options = {}) {
    if (options.autoFocusFirstField) {
      focusOnSectionOpen(isOpen, ".dietary-lines-list .dietary-input");
    }
  }
</script>

<style lang="scss" scoped>
  $amount-width-desktop: 100px;
  $unit-width-desktop: 44px;
  $amount-width-mobile: 112px;
  $unit-width-mobile: 50px;

  .dietary-line .column {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .dietary-line--indented {
    .dietary-line-label {
      padding-left: calc(0.75rem + 16px);
    }
  }

  .dietary-line--double-indented {
    .dietary-line-label {
      padding-left: calc(0.75rem + 32px);
    }
  }

  .dietary-line--warned {
    .dietary-input {
      background-color: #fffbeb;
    }

    .dietary-unit {
      background-color: #fef3c7;
    }
  }

  .dietary-line--compliance {
    .dietary-input {
      background-color: #fef2f2;
    }

    .dietary-unit {
      background-color: #fecaca;
    }
  }

  .dietary-input,
  .dietary-unit {
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 28px !important;
    box-sizing: border-box !important;
  }

  .dietary-input {
    width: $amount-width-desktop;
    flex: 0 0 $amount-width-desktop !important;
  }

  .dietary-unit {
    width: $unit-width-desktop;
    min-width: $unit-width-desktop;
    flex: 0 0 $unit-width-desktop;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .dietary-line--calories .dietary-input,
  .dietary-line--no-unit .dietary-input {
    width: calc(#{$amount-width-desktop} + #{$unit-width-desktop});
    flex-basis: calc(#{$amount-width-desktop} + #{$unit-width-desktop}) !important;
  }

  .dietary-lines-list {
    max-width: 100%;
    margin: 0 auto;
  }

  @media screen and (max-width: 1023px) {
    .dietary-lines-list {
      max-width: 380px;
      margin-left: auto;
      margin-right: auto;
    }

    .dietary-input {
      width: $amount-width-mobile;
      flex: 0 0 $amount-width-mobile !important;
    }

    .dietary-unit {
      width: $unit-width-mobile;
      min-width: $unit-width-mobile;
      flex-basis: $unit-width-mobile;
    }

    .dietary-line--calories .dietary-input,
    .dietary-line--no-unit .dietary-input {
      width: calc(#{$amount-width-mobile} + #{$unit-width-mobile});
      flex-basis: calc(#{$amount-width-mobile} + #{$unit-width-mobile}) !important;
    }
  }

  .protein-hint {
    font-size: 0.75rem;
  }

  .dietary-unit--stacked {
    flex-direction: column !important;
    line-height: 1 !important;

    .unit-part {
      font-size: 12px;
      line-height: 1.1;
      font-weight: 500;
    }
  }
</style>
